import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BingoV2Card from '../bingo/BingoV2Card';
import TutorialCelebration from './TutorialCelebration';
import { useNavigate } from 'react-router-dom';

/**
 * SimulatedBingoGame
 * Phase 1: "create-room" - Shows CreateRoomModal pre-filled, fake submit
 * Phase 2: "play" - Interactive tutorial bingo with dice roll + manual marking
 *
 * User clicks the dice to draw a number, then clicks matching cells on the card.
 * When the winning line is marked, user clicks BINGO! to celebrate.
 */
const SimulatedBingoGame = ({ phase, onRoomCreated, onBingoWon }) => {
  const navigate = useNavigate();
  const [gamePhase, setGamePhase] = useState(phase);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [currentBall, setCurrentBall] = useState(null);
  const [markedPositions, setMarkedPositions] = useState([]);
  const [canCallBingo, setCanCallBingo] = useState(false);
  const [won, setWon] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [diceRolling, setDiceRolling] = useState(false);

  // Create-room simulation states
  // 'form' | 'creating' | 'created' | 'buy-cards' | 'waiting-ready' | 'starting' | 'ready'
  const [createStep, setCreateStep] = useState('form');
  const [simulatedRoomCode, setSimulatedRoomCode] = useState('');
  const [simulatedPlayers, setSimulatedPlayers] = useState([]);
  const [playersReady, setPlayersReady] = useState([]);
  const [simulatedCountdown, setSimulatedCountdown] = useState(5);
  const [startCountdown, setStartCountdown] = useState(3);
  const [boughtCards, setBoughtCards] = useState(0);
  const [buyingCards, setBuyingCards] = useState(false);
  const timeoutRef = useRef(null);

  // Predefined card grid (5x5, 75-ball style)
  // Row 0: B-1, B-2, B-3, B-4, B-5  -> this will be the winning line
  const fakeCard = {
    id: 'tutorial-card-1',
    grid: [
      [
        { value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }, { value: 5 },
      ],
      [
        { value: 16 }, { value: 17 }, { value: 18 }, { value: 19 }, { value: 20 },
      ],
      [
        { value: 31 }, { value: 32 }, { value: 'FREE' }, { value: 34 }, { value: 35 },
      ],
      [
        { value: 46 }, { value: 47 }, { value: 48 }, { value: 49 }, { value: 50 },
      ],
      [
        { value: 61 }, { value: 62 }, { value: 63 }, { value: 64 }, { value: 65 },
      ],
    ],
    marked_positions: [],
  };

  // Winning numbers: exactly the top row
  const winningNumbers = [1, 2, 3, 4, 5];

  const handleFakeCreateRoom = useCallback(() => {
    setCreateStep('creating');
    // Simulate server request delay
    timeoutRef.current = setTimeout(() => {
      const code = 'TUT-' + Math.floor(1000 + Math.random() * 9000);
      setSimulatedRoomCode(code);
      setCreateStep('created');
    }, 1500);
  }, []);

  const handleBuyCards = useCallback((quantity) => {
    setBuyingCards(true);
    timeoutRef.current = setTimeout(() => {
      setBoughtCards(quantity);
      setBuyingCards(false);
      setCreateStep('waiting-ready');
      // Simulate other players joining and getting ready
      const fakePlayers = [
        { name: 'Ana', cards: 2, readyDelay: 800 },
        { name: 'Luis', cards: 1, readyDelay: 1200 },
        { name: 'Carlos', cards: 3, readyDelay: 600 },
        { name: 'Maria', cards: 2, readyDelay: 1500 },
        { name: 'Jose', cards: 1, readyDelay: 900 },
        { name: 'Pedro', cards: 2, readyDelay: 1100 },
      ];
      setSimulatedPlayers(fakePlayers);
      // Mark each player as ready after their delay
      fakePlayers.forEach((player, i) => {
        timeoutRef.current = setTimeout(() => {
          setPlayersReady(prev => [...prev, player.name]);
        }, 500 + (i + 1) * 600);
      });
      // After all ready, user (host) must click "Iniciar Partida"
      // No auto-advance here — button appears when all ready
    }, 800);
  }, [onRoomCreated]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        clearInterval(timeoutRef.current);
      }
    };
  }, []);

  // Roll dice: draw next winning number not yet drawn
  const handleRollDice = () => {
    if (won) return;
    if (diceRolling) return;

    const remaining = winningNumbers.filter(n => !drawnNumbers.includes(n));
    if (remaining.length === 0) return;

    setDiceRolling(true);
    setCurrentBall(null);

    // Show rolling animation for ~1s, then reveal
    timeoutRef.current = setTimeout(() => {
      const nextNum = remaining[0]; // sequential for tutorial predictability
      setCurrentBall(nextNum);
      setDrawnNumbers(prev => [...prev, nextNum]);
      setDiceRolling(false);
    }, 900);
  };

  // Handle user marking a number on the card
  const handleMarkNumber = ({ row, col }) => {
    setMarkedPositions(prev => {
      const exists = prev.some(p => p.row === row && p.col === col);
      if (exists) return prev;
      return [...prev, { row, col }];
    });
  };

  // Check if all winning cells are marked (row 0, cols 0-4)
  useEffect(() => {
    const allMarked = winningNumbers.every((num, idx) => {
      if (num === 'FREE') return true;
      return markedPositions.some(p => p.row === 0 && p.col === idx);
    });
    if (allMarked && !canCallBingo && !won) {
      setCanCallBingo(true);
    }
  }, [markedPositions, canCallBingo, won]);

  // When external phase prop changes, sync internal state
  useEffect(() => {
    setGamePhase(phase);
  }, [phase]);

  if (gamePhase === 'create-room') {
    return (
      <div className="tutorial-bingo-sim tutorial-bingo-sim-create-room">
        {createStep === 'form' && (
          <>
            <div className="tutorial-bingo-sim-header">
              <h3>🎓 Sala de práctica guiada</h3>
              <p>Ensaya el flujo del host con una demo visual antes de jugar una sala real.</p>
            </div>
            <div className="tutorial-bingo-instruction-banner">
              <span className="tutorial-bingo-banner-label">SIMULADO</span>
              <p>Esta demo simplifica pasos del juego real para enseñarte qué hace el host.</p>
            </div>
            {/* Simulated Create Room Form - NO real HTTP requests */}
            <div className="tutorial-bingo-create-room-form">
              <div className="tutorial-bingo-form-header">
                <h2>Crear Sala de Bingo</h2>
                <span className="tutorial-bingo-sim-badge">Demo</span>
              </div>
              <div className="tutorial-bingo-form-body">
                <div className="tutorial-bingo-form-row">
                  <label>Modo</label>
                  <select className="tutorial-bingo-form-select" defaultValue="75" readOnly>
                    <option value="75">75 numeros (5x5 Clasico)</option>
                    <option value="90-in-5x5">90 numeros (5x5 Ampliado)</option>
                    <option value="90">90 numeros (9x3 Britanico)</option>
                  </select>
                </div>
                <div className="tutorial-bingo-form-row">
                  <label>Patron de Victoria</label>
                  <select className="tutorial-bingo-form-select" defaultValue="line" readOnly>
                    <option value="line">Linea</option>
                    <option value="corners">Esquinas</option>
                    <option value="fullcard">Carton Completo</option>
                  </select>
                </div>
                <div className="tutorial-bingo-form-row">
                  <label>Moneda</label>
                  <select className="tutorial-bingo-form-select" defaultValue="fires" readOnly>
                    <option value="coins">Monedas</option>
                    <option value="fires">Fuegos</option>
                  </select>
                </div>
                <div className="tutorial-bingo-form-row">
                  <label>Costo por Carton</label>
                  <input
                    type="number"
                    className="tutorial-bingo-form-input"
                    defaultValue={1}
                    min={1}
                    readOnly
                  />
                </div>
                <div className="tutorial-bingo-form-row">
                  <label>Maximo de Jugadores</label>
                  <input
                    type="number"
                    className="tutorial-bingo-form-input"
                    defaultValue={30}
                    min={2}
                    max={30}
                    readOnly
                  />
                </div>
                <div className="tutorial-bingo-form-row tutorial-bingo-form-row-checkbox">
                  <input type="checkbox" id="sim-public" defaultChecked readOnly />
                  <label htmlFor="sim-public">Sala Publica</label>
                </div>
              </div>
              <div className="tutorial-bingo-form-actions">
                <button
                  className="tutorial-bingo-btn-secondary"
                  onClick={() => {}}
                >
                  Cancelar
                </button>
                <button
                  className="tutorial-bingo-btn-primary"
                  onClick={handleFakeCreateRoom}
                >
                  Crear Sala
                </button>
              </div>
            </div>
          </>
        )}

        {createStep === 'creating' && (
          <div className="tutorial-bingo-sim-state-screen">
            <div className="tutorial-bingo-spinner" />
            <h3>Creando sala...</h3>
            <p>Generando código demo y preparando la práctica guiada</p>
          </div>
        )}

        {createStep === 'created' && (
          <div className="tutorial-bingo-sim-state-screen">
            <div className="tutorial-bingo-room-code">
              <span className="tutorial-bingo-room-code-label">Codigo de sala</span>
              <span className="tutorial-bingo-room-code-value">{simulatedRoomCode}</span>
            </div>
            <h3>Sala creada!</h3>
            <p>Como host, aquí comprarías tus cartones antes de poder iniciar una sala real.</p>
            <div style={{ marginTop: 16 }}>
              <button
                className="tutorial-bingo-btn-primary"
                onClick={() => setCreateStep('buy-cards')}
              >
                Comprar Cartones
              </button>
            </div>
          </div>
        )}

        {createStep === 'buy-cards' && (
          <div className="tutorial-bingo-sim-state-screen tutorial-bingo-buy-cards">
            <h3>Comprar Cartones</h3>
            <p>Selecciona cuántos cartones quieres. En una sala real el host también necesita al menos 1 cartón para iniciar.</p>
            <div className="tutorial-bingo-card-quantity">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`tutorial-bingo-qty-btn ${boughtCards === n ? 'selected' : ''}`}
                  onClick={() => setBoughtCards(n)}
                  disabled={buyingCards}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="tutorial-bingo-cost-display">
              <span>Costo total:</span>
              <strong>{boughtCards || 1} fuego{boughtCards > 1 ? 's' : ''} en esta demo</strong>
            </div>
            <div className="tutorial-bingo-form-actions" style={{ marginTop: 16, width: '100%', maxWidth: 300 }}>
              <button
                className="tutorial-bingo-btn-secondary"
                onClick={() => setCreateStep('created')}
                disabled={buyingCards}
              >
                Volver
              </button>
              <button
                className="tutorial-bingo-btn-primary"
                onClick={() => handleBuyCards(boughtCards || 1)}
                disabled={buyingCards || boughtCards === 0}
              >
                {buyingCards ? 'Comprando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {createStep === 'waiting-ready' && (
          <div className="tutorial-bingo-sim-state-screen">
            <div className="tutorial-bingo-room-code">
              <span className="tutorial-bingo-room-code-label">Sala</span>
              <span className="tutorial-bingo-room-code-value">{simulatedRoomCode}</span>
            </div>
            <h3>Sala de Espera</h3>
            <p>Esta práctica muestra la idea general: jugadores con cartones, estado listo y host esperando el momento para iniciar.</p>
            <div className="tutorial-bingo-players-ready-list">
              <AnimatePresence>
                {/* User (you) always first and ready */}
                <motion.div
                  key="Tu"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="tutorial-bingo-player-row ready"
                >
                  <div className="tutorial-bingo-player-info">
                    <span className="tutorial-bingo-player-avatar">🙂</span>
                    <span className="tutorial-bingo-player-name">Tu</span>
                    <span className="tutorial-bingo-player-cards">{boughtCards} cartón{boughtCards > 1 ? 'es' : ''}</span>
                  </div>
                  <span className="tutorial-bingo-ready-check">✓ Listo</span>
                </motion.div>
                {simulatedPlayers.map((player, i) => (
                  <motion.div
                    key={player.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className={`tutorial-bingo-player-row ${playersReady.includes(player.name) ? 'ready' : ''}`}
                  >
                    <div className="tutorial-bingo-player-info">
                      <span className="tutorial-bingo-player-avatar">
                        {['🎮', '🎯', '🎲', '🎪', '🎨', '🎭'][i % 6]}
                      </span>
                      <span className="tutorial-bingo-player-name">{player.name}</span>
                      <span className="tutorial-bingo-player-cards">
                        {playersReady.includes(player.name)
                          ? `${player.cards} cartón${player.cards > 1 ? 'es' : ''}`
                          : 'comprando...'}
                      </span>
                    </div>
                    <span className="tutorial-bingo-ready-check">
                      {playersReady.includes(player.name) ? '✓ Listo' : '⏳'}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            <div className="tutorial-bingo-players-progress">
              <div className="tutorial-bingo-progress-bar">
                <motion.div
                  className="tutorial-bingo-progress-fill"
                  initial={{ width: '16%' }}
                  animate={{ width: `${((playersReady.length + 1) / (simulatedPlayers.length + 1)) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <span className="tutorial-bingo-progress-text">
                {playersReady.length + 1} de {simulatedPlayers.length + 1} listos
              </span>
            </div>
            {playersReady.length === simulatedPlayers.length && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{ marginTop: 16 }}
              >
                <button
                  className="tutorial-bingo-btn-start-game"
                  onClick={() => {
                    setCreateStep('starting');
                    let count = 3;
                    setStartCountdown(count);
                    const countInterval = setInterval(() => {
                      count -= 1;
                      setStartCountdown(count);
                      if (count <= 0) {
                        clearInterval(countInterval);
                        setCreateStep('ready');
                        timeoutRef.current = setTimeout(() => {
                          if (typeof onRoomCreated === 'function') onRoomCreated();
                        }, 2000);
                      }
                    }, 1000);
                  }}
                >
                  ▶ Iniciar Partida
                </button>
              </motion.div>
            )}
          </div>
        )}

        {createStep === 'starting' && (
          <div className="tutorial-bingo-sim-state-screen">
            <div className="tutorial-bingo-start-countdown">
              <motion.div
                key={startCountdown}
                initial={{ scale: 2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="tutorial-bingo-countdown-number"
              >
                {startCountdown > 0 ? startCountdown : 'GO!'}
              </motion.div>
            </div>
            <h3>Iniciando partida...</h3>
            <p>Prepárate: en una sala real aquí el host pone en marcha la partida cuando ya hay suficientes jugadores.</p>
            <div className="tutorial-bingo-players-ready-list compact">
              {[{ name: 'Tu', cards: boughtCards }, ...simulatedPlayers].map((p, i) => (
                <span key={p.name} className="tutorial-bingo-player-chip ready">
                  {p.name} ✓
                </span>
              ))}
            </div>
          </div>
        )}

        {createStep === 'ready' && (
          <div className="tutorial-bingo-sim-state-screen">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="tutorial-bingo-ready-icon"
            >
              🎉
            </motion.div>
            <h3>¡La partida comienza!</h3>
            <p>Todos los jugadores están listos. Ahora pasarás a una práctica simple de marcar y cantar Bingo.</p>
          </div>
        )}
      </div>
    );
  }

  if (gamePhase === 'play') {
    // Build card with current marked positions so BingoV2Card reflects them
    const currentCard = {
      ...fakeCard,
      marked_positions: markedPositions,
    };

    const allDrawn = winningNumbers.every(n => drawnNumbers.includes(n));

    return (
      <div className="tutorial-bingo-sim">
        <div className="tutorial-bingo-sim-header">
          <h3>Práctica de marcado y Bingo</h3>
          <p>
            {drawnNumbers.length === 0
              ? 'Esta demo usa una secuencia guiada: presiona el dado para sacar un número y luego tócalo en tu cartón.'
              : allDrawn && !canCallBingo
              ? 'Marca los números visibles en tu cartón para completar la línea de práctica.'
              : canCallBingo && !won
              ? '¡Línea completa! Presiona GRITAR BINGO para terminar esta práctica.'
              : 'Sigue marcando los números que salgan en esta demo guiada.'}
          </p>
        </div>

        {/* Drawn balls display */}
        <div className="tutorial-bingo-ball-display">
          {winningNumbers.map((n) => (
            <AnimatePresence key={n}>
              {drawnNumbers.includes(n) ? (
                <motion.div
                  className="tutorial-bingo-ball"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                >
                  {n}
                </motion.div>
              ) : (
                <div className="tutorial-bingo-ball-placeholder" key={`ph-${n}`} />
              )}
            </AnimatePresence>
          ))}
        </div>

        {/* Dice / Current ball */}
        <div className="tutorial-bingo-dice-area" style={{ textAlign: 'center', marginBottom: 12 }}>
          {!allDrawn && (
            <motion.button
              className="tutorial-bingo-dice"
              onClick={handleRollDice}
              disabled={diceRolling}
              whileTap={{ scale: 0.92 }}
              animate={diceRolling ? { rotate: [0, 360, 720] } : {}}
              transition={{ duration: 0.9 }}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: 'none',
                background: diceRolling
                  ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                  : 'linear-gradient(135deg, #22d3ee, #818cf8)',
                color: '#fff',
                fontSize: '1.6rem',
                fontWeight: 800,
                cursor: diceRolling ? 'wait' : 'pointer',
                boxShadow: diceRolling
                  ? '0 0 24px rgba(251,191,36,0.6), 0 4px 16px rgba(245,158,11,0.4)'
                  : '0 0 28px rgba(34,211,238,0.5), 0 4px 16px rgba(129,140,248,0.4)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: diceRolling ? 'none' : 'btn-pulse-glow 2s ease-in-out infinite',
              }}
            >
              {diceRolling ? '...' : currentBall ?? '🎲'}
            </motion.button>
          )}
          {currentBall !== null && !won && !diceRolling && (
            <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'rgba(226,232,240,0.7)' }}>
              Número: <strong>{currentBall}</strong> — tócalo en el cartón para seguir la práctica.
            </div>
          )}
        </div>

        {/* Card */}
        <div className="tutorial-bingo-sim-card-wrap">
          <BingoV2Card
            card={currentCard}
            cardNumber={1}
            drawnNumbers={drawnNumbers.map(String)}
            mode="75"
            onMarkNumber={handleMarkNumber}
            onCallBingo={() => {}}
            canCallBingo={false}
          />
        </div>

        {/* Call bingo CTA when line is marked */}
        <AnimatePresence>
          {canCallBingo && !won && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: 'center', marginTop: 12 }}
            >
              <button
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                  border: 'none',
                  padding: '14px 36px',
                  borderRadius: 14,
                  fontWeight: 800,
                  color: '#1e1e2f',
                  fontSize: '1.15rem',
                  boxShadow: '0 0 28px rgba(251,191,36,0.5), 0 4px 16px rgba(245,158,11,0.3)',
                  animation: 'btn-pulse-glow 1.5s ease-in-out infinite',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
                onClick={() => {
                  setWon(true);
                  setShowCelebration(true);
                }}
              >
                GRITAR BINGO!
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Celebration overlay */}
        <AnimatePresence>
          {showCelebration && (
            <TutorialCelebration
              title="BINGO!"
              subtitle="Completaste la práctica guiada de Bingo"
              durationMs={3500}
              onDone={() => {
                if (typeof onBingoWon === 'function') onBingoWon();
              }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
};

export default SimulatedBingoGame;
